import {isDevMode, LOCALE_ID, NgModule} from "@angular/core";
import {BrowserModule} from "@angular/platform-browser";
import {BrowserAnimationsModule} from "@angular/platform-browser/animations";
import {provideEffects} from "@ngrx/effects";
import {provideStore} from "@ngrx/store";
import {provideStoreDevtools} from "@ngrx/store-devtools";
import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {AssetModule} from "./asset/asset.module";
import {FundModule} from "./fund/fund.module";
import {SharedModule} from "./shared/shared.module";
import {MatIconModule} from "@angular/material/icon";

import {environment} from "src/environments/environment";

import * as aiAssistantEffects from "./shared/store/ai-assistant/ai-assistant.effects";
import * as assetEffects from "./asset/store/asset/asset.effects";
import * as assetValuationEffects from "./asset/store/asset-valuation/asset-valuation.effects";
import * as comparableCompanyEffects from "./asset/store/asset/comparable-company.effects";
import * as dealEffects from "./deal/store/deal/deal.effects";
import * as fundEffects from "./fund/store/fund/fund.effects";
import * as fundInvestmentEffects from "./fund/store/fund-investment/fund-investment.effects";
import * as fundReportEffects from "./fund/store/fund-report/fund-report.effects";
import * as fundValuationEffects from "./fund/store/fund-valuation/fund-valuation.effects";
import * as fxEffects from "./shared/store/fx/fx.effects";
import * as generalPartnerEffects from "./fund/store/general-partner/general-partner.effects";
import * as importEffects from "./import/store/import.effects";
import * as reportEffects from "./deal/store/report/report.effects";
import * as searchEffects from "./search/store/search.effects";
import * as sellerEffects from "./deal/store/seller/seller.effects";
import * as sellerPositionEffects from "./deal/store/seller-position/seller-position.effects";
import * as sharedEffects from "./shared/store/shared.effects";
import * as newsEffects from "./shared/store/news/news.effects";
import * as documentEffects from "./shared/store/document/document.effects";
import * as patternEffects from "./shared/store/pattern/pattern.effects";
import * as patternGroupEffects from "./shared/store/pattern-group/pattern-group.effects";
import * as userSettingEffects from "./shared/user-setting/store/user-setting.effects";
import * as aiChatEffects from "./shared/store/ai-chat/ai-chat.effects";
import {DATE_PIPE_DEFAULT_OPTIONS, DatePipe} from "@angular/common";
import {DecimalFormatPipe} from "./shared/pipes/decimal-format/decimal-format.pipe";
import {SearchModule} from "./search/search.module";
import {DealModule} from "./deal/deal.module";
import {DateAdapter, MAT_DATE_LOCALE} from "@angular/material/core";
import {TimestampPipe} from "./shared/pipes/timestamp/timestamp.pipe";
import {FundAiSetupModule} from "./fund-ai-setup/fund-ai-setup.module";
import * as fundAiSetupEffects from "./fund-ai-setup/store/fund-ai-setup.effects";
import {AlgorithmicDmvModule} from "./algorithmic-dmv/algorithmic-dmv.module";
import * as algorithmicDmvEffects from "./algorithmic-dmv/store/algorithmic-dmv.effects";
import * as notificationEffects from "./notification/store/notification.effects";
import * as favoriteEffects from "./favorites/store/favorite.effects";
import * as commentEffects from "./comment/store/comment.effects";
import {CustomDateAdapter} from "./shared/adapter/custom-date-adapter";
import {TraceableFormatPipe} from "./shared/pipes/traceable-format/traceable-format.pipe";
import {MatButtonModule} from "@angular/material/button";
import {MatBottomSheetModule} from "@angular/material/bottom-sheet";
import {MAT_TOOLTIP_DEFAULT_OPTIONS} from "@angular/material/tooltip";
import {AiChatPanelComponent} from "./shared/components/ai-chat-panel/ai-chat-panel.component";
import {tooltipDefaults} from "./MatDefaultOptions";
import {APP_MODULE_CONFIGURATION} from "../environments/app-module-configuration-token";
import {APP_STATE_PROVIDERS} from "./app-state.providers";

@NgModule({
    declarations: [
        AppComponent
    ],
    imports: [
        BrowserModule,
        AppRoutingModule,
        BrowserAnimationsModule,
        SharedModule,
        AssetModule,
        FundModule,
        SearchModule,
        DealModule,
        FundAiSetupModule,
        AlgorithmicDmvModule,
        MatButtonModule,
        MatBottomSheetModule,
        MatIconModule,
        AiChatPanelComponent
    ],
    providers: [
        // Root store provider (this fixes NG0201)
        provideStore({}, {
            runtimeChecks: {
                strictStateImmutability: true,
                strictActionImmutability: true,
                strictStateSerializability: true,
                strictActionSerializability: true
            }
        }),

        // Root effects
        provideEffects(
            aiAssistantEffects,
            assetEffects,
            assetValuationEffects,
            comparableCompanyEffects,
            dealEffects,
            fundEffects,
            fundInvestmentEffects,
            fundReportEffects,
            fundValuationEffects,
            fxEffects,
            generalPartnerEffects,
            importEffects,
            reportEffects,
            searchEffects,
            sellerEffects,
            sellerPositionEffects,
            sharedEffects,
            newsEffects,
            documentEffects,
            patternEffects,
            patternGroupEffects,
            patternEffects,
            userSettingEffects,
            aiChatEffects,
            fundAiSetupEffects,
            algorithmicDmvEffects,
            notificationEffects,
            favoriteEffects,
            commentEffects
        ),

        // Central feature reducers
        ...APP_STATE_PROVIDERS,

        ...(isDevMode()
            ? [provideStoreDevtools({ maxAge: 25, connectInZone: true })]
            : []),

        {provide: APP_MODULE_CONFIGURATION, useValue: environment},
        {provide: LOCALE_ID, useValue: "en-US"},
        {provide: DATE_PIPE_DEFAULT_OPTIONS, useValue: {dateFormat: "dd/MM/yyyy"}},
        {provide: MAT_DATE_LOCALE, useValue: "en-GB"},
        {provide: DatePipe},
        {provide: TraceableFormatPipe},
        {provide: DecimalFormatPipe},
        {provide: TimestampPipe},
        {provide: DateAdapter, useClass: CustomDateAdapter},
        {provide: MAT_TOOLTIP_DEFAULT_OPTIONS, useValue: tooltipDefaults}
    ],
    bootstrap: [AppComponent]
})
export class AppModule {
}

